/**
 * ConfigService implementation that reads from config.json (runtime config).
 * Use loadConfigFromJson() to fetch config and get a ConfigService instance.
 */

import type { ConfigService } from "./config.types";
import type { Config } from "./config.types";
import type { ConfigJson } from "./config-json.types";
import { parseFeatureFlags, buildFeatures } from "@/feature-flags/feature-flags";

const DEFAULT_CONFIG_URL = "/config.json";

function parseAllowedRedirectDomains(raw: string | undefined): string[] {
	if (!raw) return [];
	return raw
		.split(",")
		.map((domain) => domain.trim())
		.filter(Boolean);
}

function mapJsonToConfig(json: ConfigJson): Config {
	return {
		apiBaseUrl: json.VITE_API_BASE_URL ?? "/api",
		recaptchaSiteKey: json.VITE_RECAPTCHA_SITE_KEY ?? "",
		allowedRedirectDomains: parseAllowedRedirectDomains(json.VITE_ALLOWED_REDIRECT_DOMAINS),
		features: buildFeatures(parseFeatureFlags(json.VITE_FEATURE_MANAGEMENT)),
	};
}

/**
 * ConfigService that holds a preloaded Config (e.g. from config.json).
 */
export class JsonConfigService implements ConfigService {
	constructor(private readonly config: Config) {}

	getConfig(): Config {
		return this.config;
	}
}

/**
 * Fetches config from config.json and returns a ConfigService.
 * Use at app init, then setConfigService(service).
 *
 * @param url - Config URL (default: "/config.json")
 * @returns ConfigService backed by the loaded config
 * @throws Error if fetch or JSON parse fails
 */
export async function loadConfigFromJson(url: string = DEFAULT_CONFIG_URL): Promise<ConfigService> {
	const response = await fetch(url);

	if (!response.ok) {
		throw new Error(`Failed to load config from ${url}: ${response.status} ${response.statusText}`);
	}

	const json = (await response.json()) as ConfigJson;
	const config = mapJsonToConfig(json);
	return new JsonConfigService(config);
}
